import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import axiosInstance from "../../../lib/axios";
import { logout, setCurrentUser } from "../../../stores/slices/authSlice";
import { verifyUserPath } from "../../../config/endpoints";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { triggerLeadsPrefetch } from "../../leads/api/prefetchLeadsList";
import { fetchViewPreferences } from "@/features/settings/api/viewPreferences.api";
import { hydrateDocumentScopeMode } from "@/features/settings/api/documentScopePreference";
import {
  resolveFlatPreference,
  resolveRouteMapPreference,
} from "@/features/settings/utils/mergeViewPreference";
import { hydrateLeadCardLayout } from "@/features/leads/stores/leadCardFunctionalitySlice";
import { hydrateDynamicCardLayout } from "@/features/dynamic/store/DynamicCardFunctionalitySlice";
import { hydrateUiDensity } from "@/features/settings/stores/uiDensitySlice";
import {
  hydrateDrawerTabOrder,
  DRAWER_TABS_VIEW_KEY,
} from "@/features/settings/stores/managePreferencesSlice";
import { enableViewPrefSync } from "@/stores/middleware/viewPrefSyncMiddleware";
import { DENSITY_VIEW_KEY } from "@/lib/uiDensity";
import {
  ACCESS_TOKEN_KEY,
  readActorSession,
} from "@/features/auth/impersonation/impersonationStorage";
import { signupResumeUrl } from "@/features/auth/routes/signupResume";
import { switchBackToActor } from "@/features/auth/impersonation/impersonationActions";

// ---- RESPONSE TYPE (Adjust if needed) ----
interface VerifyResponse {
  user: any; // Replace "any" with User interface
  token: string;
}

export default function useVerify({ isFetch = true }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { mutate, isPending, data, error } = useMutation<VerifyResponse>({
    mutationFn: async () => {
      const response = await axiosInstance.post(verifyUserPath);
      return response.data as VerifyResponse;
    },

    onSuccess: async (res) => {
      const { user } = res;
      // An owner whose sign-up never finished (an old tab, a bookmarked
      // /auth-redirect) goes back to that step. Never while viewing as
      // someone else — that session belongs to the manager.
      if (!readActorSession()) {
        const resumeUrl = signupResumeUrl(user, localStorage.getItem(ACCESS_TOKEN_KEY));
        if (resumeUrl) {
          window.location.replace(resumeUrl);
          return;
        }
      }
      dispatch(setCurrentUser({ user }));
      // Only the leads trio is warmed at boot. Dashboard widget chunks
      // were previously preloaded here too, but that pulled 9 dashboard
      // chunks onto every route — including /leads — adding ~10s of
      // chunk download + CORS preflight stacking before the leads XHR
      // could fire. The dashboard now self-preloads on mount.
      triggerLeadsPrefetch();

      // Hydrate the user's DB-saved "Show/hide fields" layout (the DB is the
      // source of truth; redux-persist localStorage is just a first-paint
      // cache). Best-effort: if it fails (offline), the local layout stands.
      // enableViewPrefSync() is deferred to `finally` so this hydrate — and
      // the earlier redux-persist REHYDRATE — never echo a save straight back.
      try {
        const { preferences, tenantDefaults, globalised } =
          await fetchViewPreferences();
        // Workspace layout first, this user's own row on top (see
        // mergeViewPreference). Dispatched whenever EITHER exists, so a user
        // with no personal row still lands on the workspace arrangement.
        // With "Globalise configs" ON the order flips: the admin's layout is
        // imposed and only the person's list filters/sort stay their own.
        const lead = resolveFlatPreference(
          globalised,
          tenantDefaults.leadCardFunctionality,
          preferences.leadCardFunctionality,
        );
        if (lead) dispatch(hydrateLeadCardLayout(lead));
        const dynamic = resolveRouteMapPreference(
          globalised,
          tenantDefaults.dynamicCardFunctionality,
          preferences.dynamicCardFunctionality,
        );
        if (dynamic) dispatch(hydrateDynamicCardLayout(dynamic));
        // UI density has no localStorage mirror, so this is the only place it
        // is restored. Absent = "comfortable" (full size), already applied.
        const density =
          preferences[DENSITY_VIEW_KEY] ?? tenantDefaults[DENSITY_VIEW_KEY];
        if (density) {
          dispatch(hydrateUiDensity(density));
        }
        // Lead-view drawer tab order: this user's row, else the workspace
        // default an admin's save mirrored. The DB wins over the localStorage
        // mirror so a new device shows the same strip as the last one.
        const drawerTabs = globalised
          ? (tenantDefaults[DRAWER_TABS_VIEW_KEY] ?? preferences[DRAWER_TABS_VIEW_KEY])
          : (preferences[DRAWER_TABS_VIEW_KEY] ?? tenantDefaults[DRAWER_TABS_VIEW_KEY]);
        const order = (drawerTabs as { order?: unknown } | undefined)?.order;
        if (Array.isArray(order)) {
          dispatch(
            hydrateDrawerTabOrder(
              order.filter((v): v is string => typeof v === "string"),
            ),
          );
        }
        // "Lead or deal document?" — the answer this person asked Xale to
        // remember. Personal only, so the workspace defaults are not consulted:
        // an admin's answer must never become everyone else's.
        hydrateDocumentScopeMode(preferences);
      } catch {
        // keep whatever localStorage / defaults already hydrated
      } finally {
        enableViewPrefSync();
      }
    },

    onError: async (err) => {
      console.error("Verify failed:", err);
      // A borrowed "Switch user" session that the server no longer accepts
      // (token expired, the viewed person blocked or deleted) hands the
      // manager back their own account instead of the login page.
      if (readActorSession()) {
        toast.error("Your view-as session ended. Switching back to your account.");
        await switchBackToActor({ audit: false });
        return;
      }
      dispatch(logout());
      toast.error("Please Login..");
      navigate("/login");
    },
  });

  // Call verify automatically on page load
  useEffect(() => {
    if (isFetch) {
      mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // FIXED dependency issue ✔

  return {
    verify: mutate,
    isLoading: isPending,
    user: data?.user,
    error,
  };
}
